import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  fetchBoardRegistrations,
  getBoardRegistrations,
  getBoardRegistrationsLoading,
} from "../../../features/tournoi/tournoiSlice";
import { createCsvBlob } from "../../../utils/arrayHelper";
import {
  OverlapScope,
  computeBoardPairsOverlaps,
  findOverlapForPair,
  formatBoardDayLabel,
  groupBoardsByDay,
} from "./boardOverlapHelpers";

const SCOPE_OPTIONS: Array<{ value: OverlapScope; label: string }> = [
  { value: "all", label: "Toutes (hors annulées / remboursées)" },
  { value: "paid", label: "Payées / validées (registration, admin, offert)" },
  { value: "checked_in", label: "Pointées uniquement" },
];

const BoardOverlaps: FC = () => {
  const dispatch = useAppDispatch();
  const boards = useAppSelector(getBoardRegistrations);
  const loading = useAppSelector(getBoardRegistrationsLoading);

  const [dayKey, setDayKey] = useState("");
  const [scope, setScope] = useState<OverlapScope>("all");
  const [boardAId, setBoardAId] = useState("");
  const [boardBId, setBoardBId] = useState("");

  useEffect(() => {
    dispatch(fetchBoardRegistrations());
  }, [dispatch]);

  const handleRefresh = () => {
    void dispatch(fetchBoardRegistrations({ forceRefresh: true }));
  };

  const boardsByDay = useMemo(() => groupBoardsByDay(boards), [boards]);

  const dayKeys = useMemo(
    () =>
      Array.from(boardsByDay.keys()).sort((a, b) => a.localeCompare(b, "fr")),
    [boardsByDay]
  );

  useEffect(() => {
    if (dayKeys.length === 0) {
      setDayKey("");
      return;
    }
    if (!dayKey || !boardsByDay.has(dayKey)) {
      setDayKey(dayKeys[0]);
    }
  }, [dayKeys, dayKey, boardsByDay]);

  const boardsOfDay = useMemo(
    () => (dayKey ? boardsByDay.get(dayKey) ?? [] : []),
    [boardsByDay, dayKey]
  );

  const overlaps = useMemo(
    () => computeBoardPairsOverlaps(boardsOfDay, scope),
    [boardsOfDay, scope]
  );

  // Réinitialise la paire active quand la journée (ou les tableaux) change.
  useEffect(() => {
    if (!dayKey) return;
    const dayBoards = boardsByDay.get(dayKey) ?? [];
    if (dayBoards.length < 2) {
      setBoardAId("");
      setBoardBId("");
      return;
    }
    const dayOverlaps = computeBoardPairsOverlaps(dayBoards, scope);
    const firstWithCount = dayOverlaps.find((o) => o.count > 0) ?? dayOverlaps[0];
    if (firstWithCount) {
      setBoardAId(firstWithCount.boardAId);
      setBoardBId(firstWithCount.boardBId);
      return;
    }
    setBoardAId(String(dayBoards[0].id));
    setBoardBId(String(dayBoards[1].id));
  }, [dayKey, boardsByDay, scope]);

  const activeOverlap = useMemo(
    () =>
      boardAId && boardBId
        ? findOverlapForPair(overlaps, boardAId, boardBId)
        : undefined,
    [overlaps, boardAId, boardBId]
  );

  const handleDayChange = (event: SelectChangeEvent) => {
    setDayKey(event.target.value);
    setBoardAId("");
    setBoardBId("");
  };

  const handleSelectPair = (aId: string, bId: string) => {
    setBoardAId(aId);
    setBoardBId(bId);
  };

  const handleExport = () => {
    if (!activeOverlap) return;
    const headers = ["Nom", "Prénom", "Licence", "Club", "Dossard", "Points"];
    const rows = activeOverlap.players.map((p) => [
      p.name ?? "",
      p.surname ?? "",
      p.licenceId ?? "",
      p.club ?? "",
      p.dossard ?? "",
      p.points ?? "",
    ]);
    const blob = createCsvBlob(headers, rows);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `croisement_${activeOverlap.boardAName}_${activeOverlap.boardBName}_${dayKey}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: "80vh" }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h5" fontWeight={600}>
        Croisements d&apos;inscriptions (2 tableaux)
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Par journée : joueurs inscrits sur deux tableaux à la fois (effectif +
        liste).
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "center" }}
      >
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="overlap-day-label">Journée</InputLabel>
          <Select
            labelId="overlap-day-label"
            label="Journée"
            value={dayKey}
            onChange={handleDayChange}
            disabled={dayKeys.length === 0}
          >
            {dayKeys.map((key) => (
              <MenuItem key={key} value={key}>
                {formatBoardDayLabel(key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 320 }}>
          <InputLabel id="overlap-scope-label">Périmètre</InputLabel>
          <Select
            labelId="overlap-scope-label"
            label="Périmètre"
            value={scope}
            onChange={(e) => setScope(e.target.value as OverlapScope)}
          >
            {SCOPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Stack>

      {boardsOfDay.length < 2 ? (
        <Typography color="text.secondary">
          Moins de 2 tableaux sur cette journée — aucun croisement possible.
        </Typography>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Paires du{" "}
              {dayKey ? formatBoardDayLabel(dayKey) : "—"} (
              {boardsOfDay.length} tableaux)
            </Typography>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Tableau A</TableCell>
                    <TableCell>Tableau B</TableCell>
                    <TableCell align="right">Joueurs en commun</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overlaps.map((row) => {
                    const selected =
                      (row.boardAId === boardAId &&
                        row.boardBId === boardBId) ||
                      (row.boardAId === boardBId &&
                        row.boardBId === boardAId);
                    return (
                      <TableRow
                        key={`${row.boardAId}-${row.boardBId}`}
                        hover
                        selected={selected}
                        onClick={() =>
                          handleSelectPair(row.boardAId, row.boardBId)
                        }
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          {row.boardAName}
                          {row.boardALabel !== row.boardAName
                            ? ` — ${row.boardALabel}`
                            : ""}
                        </TableCell>
                        <TableCell>
                          {row.boardBName}
                          {row.boardBLabel !== row.boardBName
                            ? ` — ${row.boardBLabel}`
                            : ""}
                        </TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="overlap-a-label">Tableau 1</InputLabel>
              <Select
                labelId="overlap-a-label"
                label="Tableau 1"
                value={boardAId}
                onChange={(e) => setBoardAId(e.target.value)}
              >
                {boardsOfDay.map((b) => (
                  <MenuItem
                    key={String(b.id)}
                    value={String(b.id)}
                    disabled={String(b.id) === boardBId}
                  >
                    {b.name}
                    {b.label && b.label !== b.name ? ` — ${b.label}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="overlap-b-label">Tableau 2</InputLabel>
              <Select
                labelId="overlap-b-label"
                label="Tableau 2"
                value={boardBId}
                onChange={(e) => setBoardBId(e.target.value)}
              >
                {boardsOfDay.map((b) => (
                  <MenuItem
                    key={String(b.id)}
                    value={String(b.id)}
                    disabled={String(b.id) === boardAId}
                  >
                    {b.name}
                    {b.label && b.label !== b.name ? ` — ${b.label}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!activeOverlap || activeOverlap.count === 0}
            >
              Export CSV
            </Button>
          </Stack>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Détail{" "}
              {activeOverlap
                ? `${activeOverlap.boardAName} ∩ ${activeOverlap.boardBName} (${activeOverlap.count})`
                : "—"}
            </Typography>
            {!activeOverlap || activeOverlap.count === 0 ? (
              <Typography color="text.secondary">
                Aucun joueur en commun pour cette paire et ce périmètre.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nom</TableCell>
                      <TableCell>Prénom</TableCell>
                      <TableCell>Licence</TableCell>
                      <TableCell>Club</TableCell>
                      <TableCell align="right">Dossard</TableCell>
                      <TableCell align="right">Points</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeOverlap.players.map((p) => (
                      <TableRow key={String(p.id ?? p.licenceId)}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell>{p.surname}</TableCell>
                        <TableCell>{p.licenceId}</TableCell>
                        <TableCell>{p.club}</TableCell>
                        <TableCell align="right">
                          {p.dossard ?? "—"}
                        </TableCell>
                        <TableCell align="right">{p.points}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default BoardOverlaps;
